/**
 * Phase 138E — Public One-Click Payment Page (Magic Link Landing)
 *
 * Route: /pay/:invoiceId?token=<jwt>
 *
 * Lets an academy owner pay a specific overdue StreamX invoice WITHOUT
 * logging into the portal. The token comes via the daily triage email +
 * WhatsApp dispatch (services/billing_radar.py → _dispatch_direct_to_academy).
 *
 * Flow:
 *   1. Mount → GET /api/pay/{id}/context?token=... → renders the card OR
 *      a clear empty state (expired / invalid / already paid)
 *   2. User taps "Pay Now" → POST /api/pay/{id}/create-order?token=... →
 *      opens Razorpay checkout
 *   3. Razorpay handler → POST /api/pay/{id}/verify?token=... → confirms
 *      paid + auto-reactivates subscription
 *   4. Success screen with "Your StreamX access is restored" CTA → /streamx/dashboard
 *
 * Public — does NOT use the auth-aware <AppShell>. Bare minimal layout.
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  ShieldCheck, Loader2, CreditCard, CheckCircle2, AlertTriangle,
  Lock, Clock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_SCRIPT = 'https://checkout.razorpay.com/v1/checkout.js';

function loadRazorpay() {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') return resolve(false);
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const s = document.createElement('script');
    s.src = RAZORPAY_SCRIPT;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

function rupees(paise) {
  if (paise === null || paise === undefined) return '—';
  return `₹${((paise || 0) / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

// Minimalist HTTP helper. NO `api` instance so we don't drag auth headers.
async function fetchPublic(path, options = {}) {
  const r = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(data?.detail || `HTTP ${r.status}`);
    err.status = r.status;
    err.data = data;
    throw err;
  }
  return data;
}

function EmptyState({ icon: Icon, title, body, ctaLabel, ctaTo }) {
  return (
    <div className="text-center py-10" data-testid="public-pay-empty-state">
      <div className="mx-auto w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
        <Icon className="h-7 w-7 text-slate-400" />
      </div>
      <h2 className="text-lg font-black text-slate-900 mb-1">{title}</h2>
      <p className="text-sm text-slate-500 max-w-xs mx-auto leading-relaxed">{body}</p>
      {ctaTo && (
        <Link to={ctaTo} className="inline-flex items-center justify-center gap-1.5 mt-5 px-4 py-2 text-xs font-bold text-white bg-slate-900 rounded-lg">
          {ctaLabel}
        </Link>
      )}
    </div>
  );
}

export default function PublicPay() {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [contextStatus, setContextStatus] = useState(null);
  const [contextReason, setContextReason] = useState('');
  const [paying, setPaying] = useState(false);
  const [stage, setStage] = useState(''); // 'creating' | 'opening' | 'verifying'
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState(null);

  const loadContext = useCallback(async () => {
    if (!token) {
      setContextStatus('invalid');
      setContextReason('Missing token. Open the link directly from your email or WhatsApp.');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await fetchPublic(
        `/api/pay/${encodeURIComponent(invoiceId)}/context?token=${encodeURIComponent(token)}`,
      );
      setContextStatus(data.status);
      setContextReason(data.reason || '');
      if (data.status === 'valid') setCtx(data);
      if (data.status === 'already_paid') setPaid(true);
    } catch (e) {
      setContextStatus('error');
      setContextReason(e?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [invoiceId, token]);

  useEffect(() => { loadContext(); }, [loadContext]);

  const startCheckout = useCallback(async () => {
    if (paying || !ctx) return;
    setPaying(true);
    setError(null);
    setStage('creating');
    try {
      const orderRes = await fetchPublic(
        `/api/pay/${encodeURIComponent(invoiceId)}/create-order?token=${encodeURIComponent(token)}`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      const { order, key_id, mocked } = orderRes;
      if (!order || !order.id) throw new Error('Order creation failed');

      if (mocked) {
        setStage('verifying');
        await fetchPublic(
          `/api/pay/${encodeURIComponent(invoiceId)}/verify?token=${encodeURIComponent(token)}`,
          {
            method: 'POST',
            body: JSON.stringify({
              razorpay_order_id: order.id,
              razorpay_payment_id: `pay_mock_${Math.random().toString(36).slice(2, 16)}`,
              razorpay_signature: '',
            }),
          },
        );
        setPaid(true);
        setPaying(false);
        setStage('');
        return;
      }

      setStage('opening');
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) {
        throw new Error('Razorpay script failed to load.');
      }
      const options = {
        key: key_id,
        amount: order.amount,
        currency: order.currency || 'INR',
        order_id: order.id,
        name: 'StreamX by Out-Play',
        description: `StreamX invoice ${ctx.invoice.invoice_number}`,
        prefill: {},
        theme: { color: '#16a34a' },
        modal: { ondismiss: () => { setPaying(false); setStage(''); } },
        handler: async (resp) => {
          try {
            setStage('verifying');
            await fetchPublic(
              `/api/pay/${encodeURIComponent(invoiceId)}/verify?token=${encodeURIComponent(token)}`,
              {
                method: 'POST',
                body: JSON.stringify({
                  razorpay_order_id: resp.razorpay_order_id,
                  razorpay_payment_id: resp.razorpay_payment_id,
                  razorpay_signature: resp.razorpay_signature,
                }),
              },
            );
            setPaid(true);
            setPaying(false);
            setStage('');
          } catch (e) {
            setError(e?.data?.detail || e.message || 'Payment verification failed');
            setPaying(false);
            setStage('');
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (r) => {
        setError(r?.error?.description || 'Payment failed');
        setPaying(false);
        setStage('');
      });
      rzp.open();
    } catch (e) {
      setError(e?.data?.detail || e.message || 'Checkout failed');
      setPaying(false);
      setStage('');
    }
  }, [paying, ctx, invoiceId, token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50 flex items-center justify-center p-4" data-testid="public-pay-page">
      <div className="w-full max-w-md">
        {/* Brand strip */}
        <div className="text-center mb-4">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-black tracking-[0.2em] uppercase text-slate-500">
            <ShieldCheck className="h-3.5 w-3.5" /> Secure · Out-Play.in
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          {loading && (
            <div className="p-10 text-center" data-testid="public-pay-loading">
              <Loader2 className="h-7 w-7 mx-auto animate-spin text-slate-400" />
              <p className="mt-3 text-sm text-slate-500">Verifying payment link…</p>
            </div>
          )}

          {!loading && paid && (
            <div className="p-8 text-center" data-testid="public-pay-success">
              <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">Payment received</h2>
              <p className="text-sm text-slate-600 max-w-xs mx-auto leading-relaxed">
                Your StreamX subscription is being restored. Live streams, replays, and dashboard access will be unlocked within 60 seconds.
              </p>
              <Link
                to="/streamx/dashboard"
                data-testid="public-pay-success-cta"
                className="inline-flex items-center gap-2 mt-6 px-5 py-3 text-sm font-black text-white bg-emerald-600 rounded-xl shadow-md hover:bg-emerald-700 transition"
              >
                Open StreamX Dashboard →
              </Link>
            </div>
          )}

          {!loading && !paid && contextStatus === 'valid' && ctx && (
            <div className="p-7" data-testid="public-pay-card">
              <div className="text-[10px] font-black tracking-[0.2em] uppercase text-amber-700 mb-1">
                ⚠ Action Required
              </div>
              <h1 className="text-2xl font-black text-slate-900 mb-1">
                Pay to keep streaming
              </h1>
              <p className="text-xs text-slate-500 mb-5">
                {ctx.academy?.name || 'Your academy'} · One-click checkout
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-5">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-[10px] font-black tracking-[0.15em] uppercase text-amber-900">Invoice</div>
                    <div className="text-sm font-bold text-slate-800 mt-0.5" data-testid="public-pay-invoice-number">
                      {ctx.invoice.invoice_number}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {ctx.invoice.billing_period_label} · {ctx.invoice.billing_cycle}
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-amber-200">
                  <div className="text-[10px] font-black tracking-[0.15em] uppercase text-amber-900">Amount Due</div>
                  <div className="text-4xl font-black text-slate-900 tabular-nums mt-1" data-testid="public-pay-amount">
                    {rupees(ctx.invoice.total_inr_paise)}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">
                    Includes {ctx.invoice.gst_percent || 18}% GST · {rupees(ctx.invoice.gst_inr_paise)}
                  </div>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 flex gap-2" data-testid="public-pay-error">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" /> {error}
                </div>
              )}

              <button
                onClick={startCheckout}
                disabled={paying}
                data-testid="public-pay-cta"
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 text-base font-black text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-lg transition disabled:opacity-60"
              >
                {paying ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {stage === 'verifying' ? 'Activating subscription…' : stage === 'opening' ? 'Opening Razorpay…' : 'Preparing…'}
                  </>
                ) : (
                  <>
                    <CreditCard className="h-4 w-4" /> Pay {rupees(ctx.invoice.total_inr_paise)} Now
                  </>
                )}
              </button>

              <div className="mt-5 grid grid-cols-3 gap-2 text-[10px] text-slate-500">
                <div className="text-center">
                  <Lock className="h-3.5 w-3.5 mx-auto mb-1 text-emerald-600" />
                  <div>Secure Razorpay</div>
                </div>
                <div className="text-center">
                  <ShieldCheck className="h-3.5 w-3.5 mx-auto mb-1 text-emerald-600" />
                  <div>No portal login</div>
                </div>
                <div className="text-center">
                  <Clock className="h-3.5 w-3.5 mx-auto mb-1 text-emerald-600" />
                  <div>Restore &lt; 60s</div>
                </div>
              </div>
            </div>
          )}

          {!loading && !paid && contextStatus === 'expired' && (
            <EmptyState
              icon={Clock}
              title="Payment link expired"
              body="This magic link is older than 72 hours. Please log in to the StreamX portal to pay your invoice directly."
              ctaLabel="Login to StreamX"
              ctaTo="/login"
            />
          )}

          {!loading && !paid && contextStatus === 'invalid' && (
            <EmptyState
              icon={AlertTriangle}
              title="Payment link invalid"
              body={contextReason || "This payment link couldn't be verified. Please log in directly to pay."}
              ctaLabel="Login to StreamX"
              ctaTo="/login"
            />
          )}

          {!loading && !paid && contextStatus === 'invoice_not_found' && (
            <EmptyState
              icon={AlertTriangle}
              title="Invoice not found"
              body="We couldn't locate this invoice. Please log in to the portal to view your billing history."
              ctaLabel="Login to StreamX"
              ctaTo="/login"
            />
          )}

          {!loading && !paid && contextStatus === 'scope_mismatch' && (
            <EmptyState
              icon={AlertTriangle}
              title="Token does not match"
              body="This payment link points to a different invoice. Please use the latest link from your email or WhatsApp."
              ctaLabel="Login to StreamX"
              ctaTo="/login"
            />
          )}

          {!loading && !paid && contextStatus === 'error' && (
            <EmptyState
              icon={AlertTriangle}
              title="Couldn't load invoice"
              body={contextReason || 'Something went wrong. Please try again or login directly.'}
              ctaLabel="Login to StreamX"
              ctaTo="/login"
            />
          )}
        </div>

        <p className="text-center text-[10px] text-slate-400 mt-4">
          Trouble? Reply to the email or WhatsApp message you received.
        </p>
      </div>
    </div>
  );
}
